import { Palette, Theme, alpha } from "@mui/material/styles";

// ----------------------------------------------------------------------

type BackgroundBlurProps = {
  blur?: number;
  opacity?: number;
  color?: string;
};

type BackgroundGradientProps = {
  direction?: string;
  startColor?: string;
  endColor?: string;
};

interface BackgroundImageProps extends BackgroundGradientProps {
  url?: string;
}

function getDirection(value = "bottom") {
  return {
    top: "to top",
    right: "to right",
    bottom: "to bottom",
    left: "to left",
  }[value];
}

// ----------------------------------------------------------------------

export default function cssStyles(theme?: Theme) {
  return {
    bgBlur: (props?: BackgroundBlurProps) => {
      const color =
        props?.color || theme?.palette.background.default || "#000000";

      const blur = props?.blur || 6;
      const opacity = props?.opacity || 0.8;

      return {
        backdropFilter: `blur(${blur}px)`,
        WebkitBackdropFilter: `blur(${blur}px)`, // Fix on Mobile
        backgroundColor: alpha(color, opacity),
      };
    },
    bgGradient: (props?: BackgroundGradientProps) => {
      const direction = getDirection(props?.direction);
      const startColor = props?.startColor || `${alpha("#000000", 0)} 0%`;
      const endColor = props?.endColor || "#000000 75%";

      return {
        background: `linear-gradient(${direction}, ${startColor}, ${endColor});`,
      };
    },
    bgImage: (props?: BackgroundImageProps) => {
      const url = props?.url || "/assets/bg_gradient.jpg";
      const direction = getDirection(props?.direction);
      const startColor =
        props?.startColor || alpha(theme?.palette.grey[900] || "#000000", 0.88);
      const endColor =
        props?.endColor || alpha(theme?.palette.grey[900] || "#000000", 0.88);

      return {
        background: `linear-gradient(${direction}, ${startColor}, ${endColor}), url(${url})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center center",
      };
    },
    flexSpaceBetween: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    paymentGatewayCard: {
      border: `1px solid ${theme?.palette.greyShade.light}`,
      borderRadius: "8px",
      padding: 0.5,
      height: "54px",
      width: "54px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    ibanCard: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      border: `1px solid ${theme?.palette.greyShade.light}`,
      borderRadius: "12px",
      m: 1,
      padding: 1,
    },
    subscribedEventListCard: {
      height: "100%",
      minHeight: 300,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    erpSyncError: {
      all: "unset",
      fontSize: "10px",
      color: "error.main",
      p: 0,
      "&:hover": {
        textDecoration: "underline",
        color: theme?.palette.error.dark,
      },
    },
    erpOPCHeader: {
      border: "1px solid #F4F6F8",
      borderRadius: "8px",
      padding: 0.5,
      height: "54px",
      width: "54px",
      ...generalStyles.flexAlignCenter,
    },
  };
}

type TextVariant =
  | "heading"
  | "primary"
  | "secondary"
  | "error"
  | "success"
  | "label"
  | "friendly"
  | "dostOcean"
  | "actionDisabled"
  | "actionActive"
  | "disabled"
  | "errorDark"
  | "warningDark"
  | "infoDark";

type FontWeight = 400 | 500 | 600 | 700;

const colors = {
  heading: (theme: Theme) => theme.palette.text.heading,
  primary: (theme: Theme) => theme.palette.primary.light,
  label: (theme: Theme) => theme.palette.text.label,
  secondary: (theme: Theme) => theme.palette.text.secondary,
  error: (theme: Theme) => theme.palette.error.main,
  errorDark: (theme: Theme) => theme.palette.error.dark,
  warningDark: (theme: Theme) => theme.palette.warning.dark,
  infoDark: (theme: Theme) => theme.palette.info.dark,
  success: (theme: Theme) => theme.palette.success.dark,
  friendly: (theme: Theme) => theme.palette.secondary.dark,
  dostOcean: (theme: Theme) => (theme.palette as any).dostOcean,
  actionDisabled: (theme: Theme) => theme.palette.action.disabled,
  actionActive: (theme: Theme) => theme.palette.action.active,
  disabled: (theme: Theme) => theme.palette.text.disabled,
};

export const textStyle =
  (
    variant: TextVariant,
    weight: FontWeight = 400,
    size = 16,
    textOverflowWidth?: number,
  ) =>
  (theme: Theme) => ({
    color: colors[variant](theme),
    fontWeight: weight,
    fontSize: size,
    ...(textOverflowWidth !== undefined && {
      textOverflow: "ellipsis" as const,
      overflow: "hidden" as const,
      whiteSpace: "nowrap" as const,
      maxWidth: textOverflowWidth,
    }),
  });

export const generalStyles = {
  flexColumnStyle: {
    display: "flex",
    flexDirection: "column",
  },
  flexRowStyle: {
    display: "flex",
    flexDirection: "row",
  },
  flexWithGapStyle: {
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  flexBetweenStyle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  flexCenterStyle: { display: "flex", alignItems: "center" },
  flexStartCenter: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  flexAlignCenter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  flexEndStyle: {
    display: "flex",
    justifyContent: "flex-end",
  },
  menuIconWrapper: {
    mr: 1.5,
    display: "flex",
    alignItems: "center",
  },
};

export const drawerStyles = {
  width: {
    xs: "100%",
    sm: 500,
    md: 500,
    lg: 500,
    xl: 600,
  },
  borderTopLeftRadius: "16px",
  borderBottomLeftRadius: "16px",
  bgcolor: "background.paper",
  transition: (theme) =>
    theme.transitions.create("width", {
      duration: theme.transitions.duration.standard,
    }),
  boxShadow: (theme) => theme.customShadows.z24,
  display: "flex",
  flexDirection: "column",
};

export const borderGeneralStyle = {
  border: "1px solid",
  borderColor: "text.borderLine",
};

export const plainOutlinedFieldSx = (options?: {
  radius?: string;
  rootSx?: Record<string, unknown>;
  inputSx?: Record<string, unknown>;
}) => {
  const line = (theme: Theme) => theme.palette.text.borderLine;
  return {
    "& .MuiOutlinedInput-root": {
      borderRadius: options?.radius ?? "4px",
      ...(options?.rootSx ?? {}),
    },
    "& .MuiOutlinedInput-input": options?.inputSx ?? {},
    "& .MuiOutlinedInput-notchedOutline": { top: 0, borderColor: line },
    "& .MuiOutlinedInput-notchedOutline legend": { display: "none" },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: line,
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: line,
      borderWidth: 1,
    },
  };
};

export const reportCardStyles = {
  card: {
    borderRadius: "8px",
    minWidth: 260,
    height: 162,
    m: 1,
    border: "1px solid #E4E6EB",
  },
  imgBox: {
    border: "1px solid #E4E6EB",
    borderRadius: "8px",
    padding: 0.5,
    height: "54px",
    width: "54px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    ml: 1,
    fontWeight: "bold",
    fontSize: "16px",
    textOverflow: "ellipsis",
    width: 250,
    cursor: "pointer",
    height: 30,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textTransform: "capitalize",
    pr: 0.5,
  },
};

export const reviewPageRightPanelToggleButtonStyle =
  (isActive: boolean) => (theme: Theme) => ({
    textTransform: "none" as const,
    color: isActive ? theme.palette.common.white : theme.palette.text.secondary,
    ...(isActive
      ? {
          bgcolor: theme.palette.primary.dark,
          borderColor: theme.palette.primary.main,
          "&:hover": { bgcolor: theme.palette.primary.main },
        }
      : {
          borderColor: theme.palette.text.secondary,
          "&:hover": {
            bgcolor: theme.palette.action.hover,
            borderColor: theme.palette.text.disabled,
          },
        }),
  });

export const manualDocNumberStyles = {
  fontWeight: 700,
  fontSize: "14px",
  color: "primary.main",
  lineHeight: "18px",
  textOverflow: "ellipsis",
  maxWidth: 150,
  cursor: "pointer",
  textAlign: "center",
};

export const captureOverlayScanningIndicatorStyle = {
  position: "absolute",
  top: 8,
  left: 8,
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 1.5,
  py: 0.75,
  borderRadius: 1,
  bgcolor: "rgba(0,0,0,0.65)",
};
export const getOutlinedCardStyle = (palette: Palette, borderRadius = 1.5) => ({
  ...borderGeneralStyle,
  borderRadius,
  backgroundColor: palette.background.paper,
});

export const draggableRowStyle = {
  ...generalStyles.flexBetweenStyle,
  ...borderGeneralStyle,
  p: 1.5,
  borderRadius: 1,
  bgcolor: "background.paper",
  userSelect: "none" as const,
};

export const getSectionLabelStyle = (color: string) => ({
  display: "block",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase" as const,
  color,
  fontSize: "10.5px",
});

export const getConfigSectionBoxStyle = (palette: Palette) => ({
  display: "flex",
  p: 1.5,
  mt: 1,
  border: `1px solid ${palette.grey[300]}`,
  borderRadius: 1,
  bgcolor: palette.grey[50],
});

/**
 * Roomier input used by the workflow assign / reassign / approve / reject
 * dialogs. Pass as `sx` to `NewTextField` / `MyTextField` / `RHFTextField`.
 * Only targets the inner input so the root `InputBase` isn't padded twice.
 */
export const dialogInputStyle = {
  "& .MuiInputBase-input": {
    height: "24px",
    padding: "9px 16px",
    borderRadius: "8px",
    "&:focus": { borderRadius: "8px" },
  },
};
export const editIconBadgeStyle = {
  position: "absolute",
  right: -4,
  bottom: -4,
  display: "flex",
  p: 0.5,
  alignItems: "center",
  justifyContent: "center",
  width: 14,
  height: 14,
  borderRadius: "50%",
  bgcolor: "primary.main",
  color: "common.white",
};

export const pillTagChipStyle = {
  bgcolor: "text.activeTab",
  color: "secondary.darker",
  border: "1px solid",
  borderColor: "secondary.darker",
  borderRadius: "999px",
  fontWeight: 500,
  px: 0.5,
  "& .MuiChip-deleteIcon": {
    color: "secondary.darker",
    backgroundColor: "transparent",
    "&:hover": {
      color: "secondary.darker",
      backgroundColor: "transparent",
    },
  },
};

export const toggleContainerStyles = {
  display: "inline-flex",
  alignItems: "center",
  bgcolor: "text.borderLine",
  borderRadius: "20px",
  p: "3px",
};

export const togglePillStyles = (isActive: boolean) => (theme: Theme) => ({
  py: 0.6,
  px: 2,
  borderRadius: "16px",
  fontSize: 13,
  fontWeight: isActive ? 600 : 500,
  color: isActive ? theme.palette.dostOcean : theme.palette.text.secondary,
  bgcolor: isActive ? theme.palette.background.paper : "transparent",
  boxShadow: isActive ? theme.shadows[1] : "none",
  cursor: "pointer",
  userSelect: "none",
  transition: theme.transitions.create(["background-color", "color"]),
  "&:hover": !isActive ? { color: theme.palette.text.primary } : {},
});

export const tagsFieldInputStyles = (error: boolean) => (theme: Theme) => ({
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiOutlinedInput-root": {
    border: `1px solid ${
      error ? theme.palette.error.main : theme.palette.text.borderLine
    }`,
    borderRadius: "4px",
    "&:hover": {
      border: `1px solid ${
        error ? theme.palette.error.main : theme.palette.text.borderLine
      }`,
    },
    "&.Mui-focused": {
      border: `1px solid ${theme.palette.secondary.main}`,
      boxShadow: `0 0 0 0.01rem ${theme.palette.secondary.main}`,
    },
  },
});
